import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import CardImage from "./CardImage";
import { toast } from "sonner";
import api from "../lib/api";
import { downscaleImage } from "../lib/image";
import { Images, Loader2, Trash2, ScanLine, AlertCircle, Check, Plus } from "lucide-react";

const inp =
  "w-full bg-[#0B0F17] border border-slate-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors";

export default function BulkScanModal({ open, onClose, onSaved }) {
  const [stage, setStage] = useState("select"); // select | scanning | review
  const [items, setItems] = useState([]); // {file, url}
  const [results, setResults] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      items.forEach((it) => URL.revokeObjectURL(it.url));
      setStage("select");
      setItems([]);
      setResults([]);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    const mapped = files.slice(0, 20).map((file) => ({ file, url: URL.createObjectURL(file) }));
    setItems((prev) => [...prev, ...mapped].slice(0, 20));
  };

  const removeItem = (i) => {
    setItems((prev) => {
      const copy = [...prev];
      const [gone] = copy.splice(i, 1);
      if (gone) URL.revokeObjectURL(gone.url);
      return copy;
    });
  };

  const scanAll = async () => {
    if (!items.length) return;
    setStage("scanning");
    const fd = new FormData();
    const files = await Promise.all(items.map((it) => downscaleImage(it.file)));
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/cards/scan/bulk", fd);
      const mapped = (data.results || []).map((r) => ({
        ...r,
        social_links: r.social_links || [],
        include: !r.error && !!(r.name || r.company),
      }));
      setResults(mapped);
      setStage("review");
    } catch (e) {
      toast.error("Bulk scan failed. Please try again.");
      setStage("select");
    }
  };

  const setField = (i, key, val) =>
    setResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const toggleInclude = (i) =>
    setResults((prev) => prev.map((r, idx) => (idx === i ? { ...r, include: !r.include } : r)));

  const includedCount = results.filter((r) => r.include && !r.error).length;

  const saveAll = async () => {
    const chosen = results.filter((r) => r.include && !r.error);
    if (!chosen.length) {
      toast.error("Select at least one card to add");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        cards: chosen.map((r) => ({
          name: r.name || "", title: r.title || "", company: r.company || "",
          phone: r.phone || "", email: r.email || "", website: r.website || "",
          industry: r.industry || "", address: r.address || "",
          social_links: r.social_links || [], image_path: r.image_path || "",
        })),
      };
      const { data } = await api.post("/cards/bulk", payload);
      toast.success(`Added ${data.created} contact${data.created === 1 ? "" : "s"}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error("Could not save contacts");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} maxWidth="max-w-2xl" testId="bulk-scan-modal">
      <div className="p-1">
        <div className="flex items-center gap-2 mb-1">
          <Images className="w-5 h-5 text-blue-500" />
          <h2 className="font-heading text-xl font-bold">Bulk scan</h2>
        </div>

        {stage === "select" && (
          <>
            <p className="text-sm text-slate-400 mb-4">
              Snap or upload a stack of cards — AI reads them all, then you review and add in one go.
            </p>
            <label
              data-testid="bulk-file-input-label"
              className="dotted-bg flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-700 rounded-2xl py-10 cursor-pointer hover:border-blue-500 transition-colors"
            >
              <Plus className="w-8 h-8 text-slate-500" />
              <span className="text-sm text-slate-300 font-medium">Choose photos</span>
              <span className="text-xs text-slate-500">Up to 20 cards · JPG or PNG</span>
              <input
                data-testid="bulk-file-input"
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
            </label>

            {items.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4 max-h-56 overflow-y-auto no-scrollbar">
                {items.map((it, i) => (
                  <div key={i} className="relative group rounded-xl overflow-hidden border border-slate-800">
                    <img src={it.url} alt="" className="w-full h-20 object-cover" />
                    <button
                      onClick={() => removeItem(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              data-testid="bulk-scan-start-button"
              onClick={scanAll}
              disabled={!items.length}
              className="w-full mt-5 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-3 transition-colors"
            >
              <ScanLine className="w-5 h-5" /> Scan {items.length || ""} card{items.length === 1 ? "" : "s"}
            </button>
          </>
        )}

        {stage === "scanning" && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-4" />
            <p className="text-sm text-slate-300 font-medium">Reading {items.length} cards…</p>
            <p className="text-xs text-slate-500 mt-1">This takes a few seconds per card.</p>
          </div>
        )}

        {stage === "review" && (
          <>
            <p className="text-sm text-slate-400 mb-4">
              Review the details, untick any you don't want, then add them all.
            </p>
            <div className="space-y-3 max-h-[52vh] overflow-y-auto no-scrollbar pr-1">
              {results.map((r, i) => (
                <div
                  key={i}
                  data-testid={`bulk-review-row-${i}`}
                  className={`flex gap-3 rounded-xl border p-3 ${
                    r.error ? "border-red-500/30 bg-red-500/5" : r.include ? "border-blue-500/40 bg-blue-500/5" : "border-slate-800 bg-[#0B0F17]"
                  }`}
                >
                  <div className="w-16 shrink-0">
                    {r.image_path ? (
                      <CardImage path={r.image_path} className="w-16 h-12 object-cover rounded-lg border border-slate-800" />
                    ) : (
                      <div className="w-16 h-12 rounded-lg bg-slate-800" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    {r.error ? (
                      <p className="flex items-center gap-2 text-sm text-red-400 py-3">
                        <AlertCircle className="w-4 h-4" /> {r.error} — {r.filename}
                      </p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inp} placeholder="Name" value={r.name || ""} onChange={(e) => setField(i, "name", e.target.value)} data-testid={`bulk-name-${i}`} />
                        <input className={inp} placeholder="Company" value={r.company || ""} onChange={(e) => setField(i, "company", e.target.value)} data-testid={`bulk-company-${i}`} />
                        <input className={inp} placeholder="Phone" value={r.phone || ""} onChange={(e) => setField(i, "phone", e.target.value)} />
                        <input className={inp} placeholder="Email" value={r.email || ""} onChange={(e) => setField(i, "email", e.target.value)} />
                        <input className={`${inp} col-span-2`} placeholder="Industry" value={r.industry || ""} onChange={(e) => setField(i, "industry", e.target.value)} />
                      </div>
                    )}
                  </div>
                  {!r.error && (
                    <button
                      onClick={() => toggleInclude(i)}
                      data-testid={`bulk-include-${i}`}
                      className={`shrink-0 w-7 h-7 rounded-md border flex items-center justify-center transition-colors ${
                        r.include ? "bg-blue-600 border-blue-600 text-white" : "border-slate-600 text-transparent hover:border-slate-400"
                      }`}
                      aria-label="Include"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setStage("select")}
                className="px-4 py-3 rounded-xl border border-slate-700 text-slate-300 hover:text-white text-sm transition-colors"
              >
                Back
              </button>
              <button
                data-testid="bulk-save-button"
                onClick={saveAll}
                disabled={saving || includedCount === 0}
                className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-3 transition-colors"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> Add {includedCount} contact{includedCount === 1 ? "" : "s"}</>}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
